"use client"

import type React from "react"
import { useState, useEffect, useCallback, useMemo } from "react"
import { Card, CardBody, CardHeader, CardTitle, Badge, Input, Row, Col } from "reactstrap"
import DataTable, { type TableColumn } from "react-data-table-component"
import { FaPlus, FaEye, FaEdit, FaTrash, FaToggleOn, FaToggleOff, FaSearch, FaFilter } from "react-icons/fa"
import { Btn } from "../../../AbstractElements"
import { RoleService } from "../../../Service/role-management-service"
import type { Role, ListParams } from "./types"
import Swal from "sweetalert2"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"

interface RoleListProps {
  onAdd: () => void
  onEdit: (role: Role) => void
  onView: (role: Role) => void
  refreshTrigger: boolean
}

const RoleList: React.FC<RoleListProps> = ({ onAdd, onEdit, onView, refreshTrigger }) => {
  const navigate = useNavigate()

  // State management
  const [data, setData] = useState<Role[]>([])
  const [loading, setLoading] = useState(false)
  const [totalRows, setTotalRows] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [sortField, setSortField] = useState<string>("name")
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc")

  // Fetch data with enhanced parameters
  const fetchData = useCallback(async () => {
    setLoading(true)

    try {
      const params: ListParams = {
        page: currentPage,
        limit: perPage,
        search: search || undefined,
        ordering: sortDirection === "desc" ? `-${sortField}` : sortField,
      }

      if (statusFilter !== "all") {
        params.is_active = statusFilter === "active"
      }

      const response = await RoleService.getAll(params)
      setData(response.results)
      setTotalRows(response.count)
    } catch (error: any) {
      console.error("Failed to fetch roles:", error)
      toast.error("Failed to load roles")
      setData([])
      setTotalRows(0)
    } finally {
      setLoading(false)
    }
  }, [currentPage, perPage, search, statusFilter, sortField, sortDirection])

  // Effects
  useEffect(() => {
    fetchData()
  }, [fetchData, refreshTrigger])

  // Debounced search
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      setCurrentPage(1) // Reset to first page on search
      fetchData()
    }, 500)

    return () => clearTimeout(delayedSearch)
  }, [search])

  // Handle role menu management navigation
  const handleManageMenus = (roleId: number) => {
    navigate(`/role-management/${roleId}/menus`)
  }

  // Handle role status toggle
  const handleToggleStatus = async (role: Role) => {
    const action = role.is_active ? "deactivate" : "activate"
    const result = await Swal.fire({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Role?`,
      text: `Are you sure you want to ${action} "${role.name}"?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: role.is_active ? "#dc3545" : "#28a745",
      cancelButtonColor: "#6c757d",
      confirmButtonText: `Yes, ${action}!`,
      reverseButtons: true,
    })

    if (!result.isConfirmed) return

    try {
      await RoleService.toggleStatus(role.id)
      toast.success(`Role ${action}d successfully`)
      fetchData()
    } catch (error: any) {
      toast.error(`Failed to ${action} role`)
    }
  }

  // Handle role deletion
  const handleDelete = async (role: Role) => {
    const result = await Swal.fire({
      title: "Delete Role?",
      html: `
        <div class="text-start">
          <p>This will permanently remove <strong>"${role.name}"</strong>.</p>
          <div class="alert alert-warning">
            <small><i class="fa fa-exclamation-triangle"></i> This action cannot be undone!</small>
          </div>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      reverseButtons: true,
      focusCancel: true,
    })

    if (!result.isConfirmed) return

    try {
      await RoleService.delete(role.id)
      await Swal.fire({
        title: "Deleted!",
        text: "Role has been removed successfully.",
        icon: "success",
        timer: 2000,
        timerProgressBar: true,
      })
      fetchData()
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "Failed to delete role"
      Swal.fire("Error", errorMessage, "error")
    }
  }

  // Handle sorting
  const handleSort = (column: any, sortDirection: "asc" | "desc") => {
    setSortField(column.selector)
    setSortDirection(sortDirection)
  }

  // Table columns with enhanced styling
  const columns: TableColumn<Role>[] = useMemo(
    () => [
      {
        name: "Role",
        selector: (row: Role) => row.name,
        sortable: true,
        cell: (row: Role) => (
          <div className="py-2">
            <div className="fw-medium fs-6">{row.name}</div>
            {row.description && (
              <div className="small text-muted text-truncate" style={{ maxWidth: "250px" }}>
                {row.description}
              </div>
            )}
          </div>
        ),
        width: "320px",
      },
      {
        name: "Status",
        selector: (row: Role) => row.is_active,
        sortable: true,
        cell: (row: Role) => (
          <Badge color={row.is_active ? "success" : "secondary"} pill className="px-3 py-2">
            {row.is_active ? "Active" : "Inactive"}
          </Badge>
        ),
        width: "120px",
      },
      {
        name: "Menus",
        selector: (row: Role) => row.menu_count || 0,
        sortable: true,
        cell: (row: Role) => (
          <div className="text-center">
            <Badge color="info" outline className="px-3 py-2">
              {row.menu_count || 0}
            </Badge>
          </div>
        ),
        width: "150px",
      },
      
      
      {
        name: "Actions",
        cell: (row: Role) => (
          <div className="d-flex gap-1 text-end">
            <Btn color="info" size="xs" onClick={() => onView(row)} title="View Details" className="btn-icon">
              <FaEye size={12} />
            </Btn>
            <Btn color="warning" size="xs" onClick={() => onEdit(row)} title="Edit Role" className="btn-icon">
              <FaEdit size={12} />
            </Btn>
            <Btn
              color="success"
              size="xs"
              onClick={() => handleManageMenus(row.id)}
              title="Manage Menus"
              className="btn-icon"
            >
              <i className="fa fa-list" style={{ fontSize: "12px" }} />
            </Btn>
            {/* <Btn
              color={row.is_active ? "secondary" : "success"}
              size="sm"
              onClick={() => handleToggleStatus(row)}
              title={row.is_active ? "Deactivate" : "Activate"}
              className="btn-icon"
            >
              {row.is_active ? <FaToggleOff size={12} /> : <FaToggleOn size={12} />}
            </Btn> */}
            <Btn color="danger" size="xs" onClick={() => handleDelete(row)} title="Delete Role" className="btn-icon">
              <FaTrash size={12} />
            </Btn>
          </div>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        width: "220px",
      },
    ],
    [onView, onEdit],
  )

  return (
    <Card className="shadow-sm">
      <CardHeader className="bg-light">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <CardTitle tag="h4" className="mb-1 text-primary">
              Role Management
            </CardTitle>
            <p className="text-muted mb-0 small">Manage system roles and permissions • {totalRows} total roles</p>
          </div>
          <Btn color="primary" onClick={onAdd} className="btn-lg">
            <FaPlus className="me-2" />
            Add New Role
          </Btn>
        </div>
      </CardHeader>

      <CardBody>
        {/* Enhanced Filters */}
        <Row className="mb-4">
          <Col md="6">
            <div className="position-relative">
              <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
              <Input
                type="search"
                placeholder="Search roles by name or description..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="ps-5"
              />
            </div>
          </Col>
          <Col md="3">
            <div className="position-relative">
              <FaFilter className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
              <Input
                type="select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="ps-5"
              >
                <option value="all">All Status</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </Input>
            </div>
          </Col>
          <Col md="3">
            <div className="text-end">
              <small className="text-muted">
                Showing {data.length} of {totalRows} roles
              </small>
            </div>
          </Col>
        </Row>

        {/* Enhanced Data Table */}
        <DataTable
          columns={columns}
          data={data}
          progressPending={loading}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationPerPage={perPage}
          paginationDefaultPage={currentPage}
          paginationRowsPerPageOptions={[10, 25, 50, 100]}
          onChangePage={setCurrentPage}
          onChangeRowsPerPage={(newPerPage, page) => {
            setPerPage(newPerPage)
            setCurrentPage(page)
          }}
          onSort={handleSort}
          sortServer
          highlightOnHover
          striped
          responsive
          noDataComponent={
            <div className="text-center py-5">
              <div className="text-muted mb-3">
                <i className="fa fa-users fa-3x"></i>
              </div>
              <h5 className="text-muted">No roles found</h5>
              <p className="text-muted">
                {search ? "Try adjusting your search criteria" : "Get started by creating your first role"}
              </p>
              {!search && (
                <Btn color="primary" onClick={onAdd} className="mt-2">
                  <FaPlus className="me-2" />
                  Create First Role
                </Btn>
              )}
            </div>
          }
          customStyles={{
            headRow: {
              style: {
                backgroundColor: "#f8f9fa",
                borderBottom: "2px solid #dee2e6",
              },
            },
            headCells: {
              style: {
                fontWeight: "600",
                fontSize: "0.875rem",
                color: "#495057",
              },
            },
            rows: {
              style: {
                minHeight: "60px",
              },
            },
          }}
        />
      </CardBody>
    </Card>
  )
}

export default RoleList
